import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Card } from '@ovComponents/2-component/card/card';
import { DashboardCard } from '@ovComponents/2-component/dashboardCard';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { getSnapshotToneColors } from '@utils/getSnapshotToneColors';
import { useTranslation } from 'react-i18next';
import type { SnapshotCardData } from './types';

export const SnapshotCard = ({
  title,
  primaryValue,
  tone = 'neutral',
  surface = 'accentBorder',
  secondaryStat,
  breakdown,
  footer,
  onClick,
  compact = false,
}: SnapshotCardData): JSX.Element => {
  const { t } = useTranslation('snapshotCard');
  const { sys } = useThemeTokens();
  const toneColors = getSnapshotToneColors(
    tone,
    sys as unknown as { color: Record<string, string> },
  );
  const isAccentBorder = surface === 'accentBorder';
  // Left-border accent uses the strong tone colour; neutral has no strong tone
  // so it falls back to the outline token (mirrors the dashboard summary-card
  // chrome in todaysActivityTab.tsx).
  const accentBorderColor =
    tone === 'neutral'
      ? (sys.color.outlineVariant ?? '')
      : toneColors.valueColor;

  const valueNode = (
    <Box
      component='span'
      sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 1 }}
    >
      <Box
        component='span'
        sx={{
          color: toneColors.valueColor,
          // Bring the value down from headingLarge toward the v1 card's
          // headingMedium so the compact card stays short.
          ...(compact ? { fontSize: '1.5rem', lineHeight: 1.2 } : {}),
        }}
      >
        {primaryValue}
      </Box>
      {secondaryStat !== undefined && (
        <Typography
          component='span'
          variant='labelSmall'
          sx={{ color: sys.color.onSurfaceVariant }}
        >
          {secondaryStat}
        </Typography>
      )}
    </Box>
  );

  return (
    <Box
      role={onClick && 'button'}
      tabIndex={onClick && 0}
      aria-label={onClick && t('clickableSnapshotCard')}
      onClick={onClick && onClick}
      sx={{
        borderRadius: '12px',
        height: '100%',
        ...(onClick ? { cursor: 'pointer' } : {}),
      }}
    >
      <Card
        variant='elevated'
        hover={!!onClick}
        sx={{
          height: '100%',
          ...(isAccentBorder
            ? {
                backgroundColor: sys.color.surface,
                borderLeft: `3px solid ${accentBorderColor}`,
              }
            : { backgroundColor: toneColors.background }),
          // Hide DashboardCard's empty description row — SnapshotCard never
          // uses it, but the third Typography still participates in gap layout.
          '& .MuiCardContent-root .MuiTypography-root:empty': {
            display: 'none',
          },
          ...(compact
            ? {
                '& .MuiCardContent-root': {
                  py: 1.25,
                  '&:last-child': { pb: 1 },
                },
                '& .MuiCardContent-root > .MuiBox-root': { gap: 0.25 },
              }
            : {}),
        }}
      >
        <DashboardCard
          title={
            // `component='span'` keeps HTML valid inside DashboardCard's outer
            // `<p>`; `colorVariant='variant'` matches the muted breakdown labels.
            <Typography
              component='span'
              variant='bodyLarge'
              colorVariant='variant'
            >
              {title}
            </Typography>
          }
          value={valueNode}
          description={null}
          colors={{
            background: 'transparent',
            title: sys.color.onSurfaceVariant ?? '',
            value: sys.color.onSurface ?? '',
            description: sys.color.onSurface ?? '',
          }}
        />
        {footer && (
          <Box
            sx={{
              px: compact ? 2 : 2.5,
              pb: compact ? 1 : 2,
              pt: 0,
              mt: breakdown?.length ? 0 : -0.25,
            }}
          >
            {typeof footer === 'string' ? (
              <Typography
                variant='labelSmall'
                sx={{ color: sys.color.onSurfaceVariant }}
              >
                {footer}
              </Typography>
            ) : (
              footer
            )}
          </Box>
        )}
        {breakdown && breakdown.length > 0 && (
          <Box sx={{ px: compact ? 2 : 2.5, pb: compact ? 1 : 2, pt: 0 }}>
            {/* Thin hairline divider separating the primary value from the breakdown rows. */}
            <Box
              sx={{
                borderTop: '1px solid',
                borderColor: sys.color.outlineVariant,
                mb: compact ? 0.5 : 1,
              }}
            />
            {breakdown.map((row, i) => {
              const rowKey =
                typeof row.label === 'string'
                  ? row.label
                  : `breakdown-row-${i}`;
              return (
                <Box
                  key={rowKey}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: compact ? 0.125 : 0.5,
                  }}
                >
                  <Typography
                    variant='labelSmall'
                    sx={{ color: sys.color.onSurface }}
                  >
                    {row.label}
                  </Typography>
                  <Typography
                    variant='labelSmall'
                    sx={{ color: sys.color.onSurface, fontWeight: 600 }}
                  >
                    {row.value}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </Card>
    </Box>
  );
};
